import {
  Button,
  Icon,
  Table,
  addToast,
  showDialogConfirm,
  useMobile,
} from '@octano/global-ui';
import {
  CellFormatOptions,
  ColumnTable,
  PaginationType,
} from '@octano/global-ui/dist/components/Table/types/TableTypes';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  deleteCampusRequest,
  getCampusRequest,
} from '../../../api/requests/campus';
import DisplayError from '../../../components/info/DisplayError';
import Loading from '../../../components/info/Loading';
import { TextEllipsis } from '../../../components/text';
import TableEmptyContent from '../../../components/text/TableEmptyContent';
import { useLayoutState } from '../../../hooks/useLayoutState';
import { useLoadingState } from '../../../hooks/useLoadingState';
import { CampusBody } from '../../../types/CampusTypes';
import { PaginationRequestType } from '../../../types/paginationRequestType';
import CampusModal from './CampusModal';

const ITEMS_PER_PAGE_DEFAULT = 10;
const PAGE_INITIAL_DEFAULT = 1;

type CampusListProps = {
  siesEnabled?: boolean;
};

const CampusList = ({ siesEnabled = false }: CampusListProps) => {
  const { showTabs } = useLayoutState();
  const isMobile = useMobile();
  const [showForm, setShowForm] = useState<boolean>(false);
  const [recordToEdit, setRecordToEdit] = useState(null);

  useEffect(() => {
    showTabs();
  }, [showTabs]);

  useEffect(() => {
    if (!showForm) {
      setRecordToEdit(null);
    }
  }, [showForm]);

  const { t } = useTranslation();

  const [campusData, setCampusData] = useState<
    PaginationRequestType<CampusBody>
  >({
    data: [],
    total_pages: 0,
    total: 0,
  });

  const [currentPage, setCurrentPage] = useState<number>(PAGE_INITIAL_DEFAULT);
  const [itemsPerPage] = useState<number>(ITEMS_PER_PAGE_DEFAULT);
  const {
    loading,
    setLoading,
    errorLoading,
    setErrorLoading,
  } = useLoadingState();

  const getCampusList = useCallback(async () => {
    setShowForm(false);
    setRecordToEdit(null);
    const { error, data: response } = await getCampusRequest(
      itemsPerPage,
      currentPage - 1,
    );
    if (response) {
      setCampusData(response);
      setErrorLoading(undefined);
    } else if (error) {
      setErrorLoading(error.code);
      return;
    }
    setLoading(false);
  }, [itemsPerPage, currentPage, setErrorLoading, setLoading]);

  const handleDelete = useCallback(
    async (row: CampusBody & { id: number }) => {
      showDialogConfirm({
        icon: { name: 'warning', color: 'warning' },
        title: t('maintainers.campusList.deleteConfirmTitle'),
        subtitle: t('maintainers.campusList.deleteConfirmSubtitle'),
        btnConfirm: {
          text: t('common.actions.confirm'),
          onConfirm: async () => {
            const { error } = await deleteCampusRequest(row.id);
            if (error) {
              addToast({
                icon: 'error',
                color: 'danger',
                text: t('maintainers.campusList.deleteFailed'),
              });
            } else {
              addToast({
                icon: 'success',
                color: 'success',
                text: t('maintainers.campusList.deleteSuccess'),
              });
              getCampusList();
            }
          },
        },
        btnClose: { text: t('common.actions.cancel') },
      });
    },
    [t, getCampusList],
  );

  useEffect(() => {
    getCampusList();
  }, [getCampusList]);

  const handlerOnChangePage = useCallback(
    async (page: number) => setCurrentPage(page),
    [],
  );

  const pagination = useMemo<PaginationType | undefined>(() => {
    if (campusData.total > itemsPerPage) {
      return {
        totalItems: campusData.total,
        itemsPerPage: itemsPerPage,
        totalPages: campusData.total_pages,
        currentPage: currentPage,
        onChangePage: handlerOnChangePage,
      };
    }
  }, [campusData, itemsPerPage, currentPage, handlerOnChangePage]);

  const columns = useMemo<ColumnTable[]>(() => {
    const editColumn = {
      columnName: 'id',
      headerText: '',
      width: '0%',
      cellFormat: (options: CellFormatOptions) => (
        <span
          onClick={() => {
            setRecordToEdit(options.row);
            setShowForm(true);
          }}
          className="cursor-pointer"
        >
          <Icon name="edit" key="edit" color="secondary" />
        </span>
      ),
    };

    const deleteColumn = {
      columnName: 'id',
      headerText: '',
      width: '0%',
      cellFormat: (options: CellFormatOptions) => (
        <span
          onClick={() => handleDelete(options.row)}
          className="cursor-pointer"
        >
          <Icon name="trash" key="trash" color="secondary" />
        </span>
      ),
    };

    const siesColumns = siesEnabled
      ? [
          {
            columnName: 'siesName',
            headerText: t('common.forms.siesName'),
            width: '15%',
          },
          {
            columnName: 'siesCode',
            headerText: t('common.forms.siesCode'),
            width: '10%',
          },
        ]
      : [];

    return !isMobile
      ? [
          {
            columnName: 'name',
            headerText: t('common.forms.campusName'),
            width: '20%',
          },
          {
            columnName: 'code',
            headerText: t('common.forms.campusCode'),
            bold: true,
            width: '15%',
          },
          {
            columnName: 'addressStreet',
            headerText: t('common.forms.address'),
            cellFormat: (options: CellFormatOptions) => {
              const { addressNumber, commune, addressExtra } = options.row;
              const addressExtraText = addressExtra ? ` - ${addressExtra}` : '';
              return (
                <TextEllipsis key={options.index} textWidth={350}>
                  {`${options.value} ${addressNumber}, ${commune.name}${addressExtraText}`}
                </TextEllipsis>
              );
            },
          },
          ...siesColumns,
          editColumn,
          deleteColumn,
        ]
      : [
          {
            columnName: 'name',
            headerText: t('common.forms.campusName'),
            width: '100%',
            cellFormat: (options: CellFormatOptions) => {
              return <div className="text-left">{options.value}</div>;
            },
          },
          editColumn,
          deleteColumn,
        ];
  }, [t, isMobile, siesEnabled, handleDelete]);

  if (errorLoading) {
    return (
      <DisplayError
        insideCard
        textBody={errorLoading}
        retryAction={() => setLoading(true)}
        loadingAction={loading}
      />
    );
  }
  if (loading) {
    return <Loading insideCard />;
  }
  return (
    <div className="g-table-container">
      <Button
        text={t('maintainers.campusForm.addTitle')}
        type="button"
        size="sm"
        icon="plus"
        className="g-add-button mb-3 mt-3"
        onClick={() => setShowForm(true)}
      />
      <Table
        columns={columns}
        data={campusData.data}
        pagination={pagination}
        noResultsText={
          <TableEmptyContent
            title={t('maintainers.campusList.campusTableEmptyTitle')}
            subtitle={t('maintainers.campusList.campusTableEmptySubtitle')}
          />
        }
      />
      <CampusModal
        siesEnabled={siesEnabled}
        show={showForm}
        reloadCampusList={getCampusList}
        toggleModal={() => {
          setShowForm(!showForm);
          setRecordToEdit(null);
        }}
        recordToEdit={recordToEdit}
      />
    </div>
  );
};

export default CampusList;
